
import React, { useEffect, useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import EligibilityAlert from '@/components/payment/EligibilityAlert';
import PaymentRequestForm from '@/components/payment/PaymentRequestForm';
import { useAuthor } from '@/hooks/useAuthors';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PAYMENT_METHODS } from '@/utils/constants';

const PaymentRequest: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const userId = user?.id || '';
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  
  console.log('PaymentRequest: User ID:', userId);
  
  // Get author data to access balance
  const { data: authorData, isLoading: isLoadingAuthor } = useAuthor(userId);
  
  // Get payment methods for the user
  const { data: paymentMethods, isLoading: isLoadingMethods } = usePaymentMethods(userId);
  
  console.log('PaymentRequest: Payment methods:', paymentMethods);
  console.log('PaymentRequest: Author data:', authorData);
  
  // Get the default method or first available method
  const defaultMethod = paymentMethods?.find(method => method.is_default) || paymentMethods?.[0];
  
  // Set selected method to default on load
  useEffect(() => {
    if (defaultMethod && !selectedMethodId) {
      setSelectedMethodId(defaultMethod.id);
      console.log('PaymentRequest: Set default method:', defaultMethod.id);
    }
  }, [defaultMethod, selectedMethodId]);
  
  // Get the currently selected method
  const selectedMethod = selectedMethodId 
    ? paymentMethods?.find(method => method.id === selectedMethodId)
    : defaultMethod;
  
  // Calculate threshold using constants to ensure consistency
  const getThresholdForMethod = (method: any) => {
    if (!method) return 0;
    const constantMethod = Object.values(PAYMENT_METHODS).find(m => m.type === method.type);
    return constantMethod ? constantMethod.threshold : method.threshold || 0;
  };
  
  const threshold = getThresholdForMethod(selectedMethod);
  const balance = authorData?.balance || 0;
  
  const isEligible = balance >= threshold;
  
  console.log('PaymentRequest: Is eligible:', isEligible, 'Balance:', balance, 'Threshold:', threshold);
  
  if (isLoadingMethods || isLoadingAuthor) {
    return (
      <MainLayout allowedRoles={['author']}>
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 mr-2 animate-spin" />
          <p>Loading payment information...</p>
        </div>
      </MainLayout>
    );
  }
  
  const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;
  
  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader 
        title="Request Payment" 
        description="Submit a request to receive your royalty earnings"
      />
      
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Payment Request Form</CardTitle>
          </CardHeader>
          <CardContent>
            <EligibilityAlert 
              isEligible={isEligible}
              balance={balance}
              threshold={threshold}
              paymentMethodType={selectedMethod?.type}
            />
            
            {!hasPaymentMethods && (
              <div className="text-center p-6 bg-gray-50 rounded-md">
                <p className="mb-4">You need to set up a payment method before requesting payments.</p>
                <Button 
                  onClick={() => navigate('/author/payment-preferences')}
                  variant="outline"
                >
                  Set Up Payment Method
                </Button>
              </div>
            )}
            
            {hasPaymentMethods && (
              <PaymentRequestForm
                paymentMethods={paymentMethods}
                defaultMethod={defaultMethod}
                isEligible={isEligible}
                threshold={threshold}
                balance={balance}
                isLoadingMethods={isLoadingMethods}
                onMethodChange={setSelectedMethodId}
                selectedMethodId={selectedMethodId}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default PaymentRequest;
